(function () {
  'use strict';

  if (window.DFN_PRODUCT_FITMENT_BOOTSTRAPPED) return;
  window.DFN_PRODUCT_FITMENT_BOOTSTRAPPED = true;

  const API_BASE = '/apps/tecdoc';
  const STORAGE_KEY = 'dfn_vehicle';
  const REQUEST_CACHE_PREFIX = 'dfn_product_fitment_cache:v2:';
  const REQUEST_CACHE_TTL = 15 * 60 * 1000;
  const fitmentDetailsCache = new Map();
  const productCompatibilityCache = new Map();
  const requestCache = new Map();
  const inflightRequests = new Map();

  function qs(root, selector) {
    return root ? root.querySelector(selector) : null;
  }

  function qsa(root, selector) {
    return root ? Array.from(root.querySelectorAll(selector)) : [];
  }

  function esc(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function readSessionCache(key) {
    try {
      const raw = sessionStorage.getItem(`${REQUEST_CACHE_PREFIX}${key}`);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed || parsed.expiresAt <= Date.now()) {
        sessionStorage.removeItem(`${REQUEST_CACHE_PREFIX}${key}`);
        return null;
      }

      return parsed.value;
    } catch (_) {
      return null;
    }
  }

  function writeSessionCache(key, value, ttl) {
    try {
      sessionStorage.setItem(`${REQUEST_CACHE_PREFIX}${key}`, JSON.stringify({
        value,
        expiresAt: Date.now() + ttl
      }));
    } catch (_) {}
  }

  function createApiError(response, json, fallbackMessage) {
    const error = new Error((json && json.error) || fallbackMessage || 'API error');
    error.status = response && typeof response.status === 'number' ? response.status : 0;
    error.code = json && json.code ? json.code : '';
    error.isApiError = true;
    return error;
  }

  function isConnectionError(error) {
    const message = String(error && error.message || '').toLowerCase();
    return error instanceof TypeError
      || message.includes('failed to fetch')
      || message.includes('networkerror')
      || message.includes('load failed');
  }

  function isTechnicalApiMessage(message) {
    const value = String(message || '').toLowerCase();
    return value.startsWith('api error')
      || value.includes('http ')
      || value.includes('rate limit')
      || value.includes('too many requests');
  }

  function resolveUserErrorMessage(root, error, fallback) {
    const status = Number(error && error.status || 0);
    const message = String(error && error.message || '').trim();
    const generic = fallback || readText(root, 'apiGenericText', 'Die Daten konnten gerade nicht geladen werden. Bitte versuchen Sie es erneut.');

    if (status === 429 || /(^|\D)429(\D|$)/.test(message) || /rate limit|too many requests/i.test(message)) {
      return readText(root, 'apiBusyText', 'Gerade sind zu viele Anfragen offen. Bitte versuchen Sie es in wenigen Sekunden erneut.');
    }

    if (status >= 500 || status === 401 || status === 403) {
      return readText(root, 'apiUnavailableText', 'Der Fahrzeugservice antwortet gerade nicht. Bitte versuchen Sie es in wenigen Minuten erneut.');
    }

    if (status === 404) {
      return readText(root, 'apiNotFoundText', 'Zu diesem Produkt konnten wir gerade keine Fahrzeugdaten laden. Bitte versuchen Sie es erneut.');
    }

    if (isConnectionError(error)) {
      return readText(root, 'apiConnectionText', 'Die Verbindung zum Fahrzeugservice ist gerade unterbrochen. Bitte versuchen Sie es erneut.');
    }

    if (error && error.isApiError) {
      return generic;
    }

    if (isTechnicalApiMessage(message)) {
      return generic;
    }

    return message || generic;
  }

  function readText(root, key, fallback) {
    const value = root && root.dataset ? root.dataset[key] : '';
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : fallback;
  }

  function formatText(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, (_, key) => {
      const value = values && Object.prototype.hasOwnProperty.call(values, key) ? values[key] : '';
      return value == null ? '' : String(value);
    });
  }

  function displaySku(sku) {
    const value = String(sku || '').trim();
    if (!value) return '';
    return value
      .replace(/-\d+(?:-\d+)?(?:_\d+)?$/i, '')
      .replace(/_\d+$/i, '');
  }

  function getSavedVehicle() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function clearSavedVehicle() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}

    document.dispatchEvent(new CustomEvent('dfn:vehicle-cleared'));
  }

  function openFinder() {
    document.dispatchEvent(new CustomEvent('dfn:open-finder'));

    const shell = qs(document, '[data-dfn-header-shell="true"]');
    if (shell) {
      shell.classList.remove('dfn-hidden');
      window.requestAnimationFrame(() => {
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
        if (!isMobile) {
          shell.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        const trigger = qs(shell, '[data-dfn-role="header-open"]') || qs(shell, '[data-dfn-role="header-change"]');
        if (trigger) trigger.click();
        const input = qs(shell, '[data-dfn-role="header-plate-input"]')
          || qs(shell, '[data-dfn-role="header-hsn-input"]')
          || qs(shell, '[data-dfn-role="vehicle-make"] input')
          || qs(shell, '[data-dfn-role="vehicle-make"] select')
          || qs(shell, '[data-dfn-role="make-select"]')
          || qs(shell, 'select, input');
        if (input && typeof input.focus === 'function') input.focus({ preventScroll: true });
      });
      return;
    }

    const finderRoot = qs(document, '[data-dfn-finder-root="true"]');
    if (finderRoot) {
      const isMobile = window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
      if (!isMobile) {
        finderRoot.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      const input = qs(finderRoot, 'select, input, button');
      if (input && typeof input.focus === 'function') input.focus({ preventScroll: true });
    }
  }

  function vehicleId(vehicle) {
    return vehicle && (vehicle.vehicleId || vehicle.id || vehicle.Id);
  }

  function vehicleName(vehicle) {
    return [
      vehicle.makeName || vehicle.MakeName || '',
      vehicle.modelName || vehicle.ModelName || '',
      vehicle.typeName || vehicle.TypeName || ''
    ].filter(Boolean).join(' ');
  }

  function vehicleMeta(vehicle) {
    return [
      vehicle.engineCode || vehicle.EngineCode || '',
      (vehicle.powerHp || vehicle.PowerHp) ? `${vehicle.powerHp || vehicle.PowerHp} PS` : '',
      vehicle.yearFrom || vehicle.YearFrom
        ? `${vehicle.yearFrom || vehicle.YearFrom}${vehicle.yearTo || vehicle.YearTo ? `-${vehicle.yearTo || vehicle.YearTo}` : ''}`
        : ''
    ].filter(Boolean).join(' · ');
  }

  function vehicleImage(vehicle) {
    return vehicle && (vehicle.imageUrl || vehicle.ImageUrl || '');
  }

  function proxyVehicleImage(url) {
    const value = String(url || '').trim();
    if (!value) return '';
    if (value.startsWith('/')) return value;
    return `${API_BASE}/vehicle-image?url=${encodeURIComponent(value)}`;
  }

  function applicationConditions(root, vehicle) {
    const alternatives = vehicle && (vehicle.applicationAlternatives || vehicle.ApplicationAlternatives);
    const restricted = Array.isArray(alternatives) && alternatives.length > 0 && alternatives.every(branch => branch.length > 0);
    const language = String(root.dataset.language || document.documentElement.lang || 'de').slice(0, 2);
    const french = language === 'fr';
    const labels = french
      ? { 3: 'Ligne d’équipement / variante', 20: 'Production à partir de', 21: "Production jusqu’à", 25: 'N° VIN à partir de', 26: 'N° VIN jusqu’à', 30: 'N° moteur à partir de', 31: 'N° moteur jusqu’à', 33: 'Code moteur', 40: 'Type de boîte de vitesses', 45: 'OPR à partir de', 46: "OPR jusqu’à", 48: 'Référence OE', 51: 'N° RICAMBI à partir de', 100: 'Position de montage', 122: "Montage d’origine", 227: 'Quantité nécessaire', 285: 'Type de catalyseur', 517: 'Lettre distinctive', 519: 'Norme antipollution', 565: 'Équipement du véhicule', 590: 'Au choix', 874: 'Non adapté au code de transmission', 885: 'Numéro de composant', 1059: 'Version', 1197: 'Code PR', 1269: 'Non adapté à la norme antipollution', 1321: 'Année-modèle jusqu’à', 1542: 'Pour code d’équipement spécial', 1543: 'Non adapté au code d’équipement spécial', 3832: 'Pour code de modèle', 4078: 'Dénomination commerciale du moteur', 4928: 'Code de modèle/type' }
      : { 3: 'Ausstattungslinie / Variante', 20: 'Baujahr ab', 21: 'Baujahr bis', 25: 'Fahrgestellnummer ab', 26: 'Fahrgestellnummer bis', 30: 'Motornummer ab', 31: 'Motornummer bis', 33: 'Motorcode', 40: 'Getriebeart', 45: 'OPR ab', 46: 'OPR bis', 48: 'OE-Nummer', 51: 'RICAMBI-Nummer ab', 100: 'Einbauposition', 122: 'Herstellereinschränkung', 227: 'benötigte Stückzahl', 285: 'Katalysatorart', 517: 'Kennbuchstabe', 519: 'Abgasnorm', 565: 'Fahrzeugausstattung', 590: 'wahlweise', 874: 'nicht für Getriebecode', 885: 'Komponentennummer', 1059: 'Version', 1197: 'PR-Nummer', 1269: 'nicht für Abgasnorm', 1321: 'Modelljahr bis', 1542: 'für SA-Code', 1543: 'nicht für SA-Code', 3832: 'für Modellcode', 4078: 'Motor-Verkaufsbezeichnung', 4928: 'Modellcode/Typcode' };
    const title = french ? 'Conditions à vérifier' : 'Einschränkungen prüfen';
    const lines = restricted ? alternatives.map(branch => branch.map(c => {
      const id = c.criterionId || c.CriterionId;
      let value = String(c.value || c.Value || '');
      if (id === 590 && value === 'true') value = french ? 'Oui' : 'Ja';
      if ((id === 20 || id === 21) && /^\d{6}$/.test(value)) value = `${value.slice(4)}/${value.slice(0, 4)}`;
      return `${labels[id] || id}: ${value}`;
    }).join(' · ')) : [];
    const target = vehicle && (vehicle.linkageTargetType || vehicle.LinkageTargetType);
    const targetLabel = target === 'M' ? ({de: 'Motor', en: 'Engine', fr: 'Moteur', it: 'Motore', es: 'Motor'}[language] || 'Engine') : target === 'O' ? (french ? 'Poids lourd' : 'Nutzfahrzeug') : '';
    return { restricted, title, html: `${targetLabel ? `<p>${esc(targetLabel)}</p>` : ''}${restricted
      ? `<div class="dfn-product-fitment-details__conditions"><strong>${esc(title)}</strong>${lines.map(line => `<p>${esc(line)}</p>`).join(`<p>${french ? 'ou' : 'oder'}</p>`)}</div>` : ''}` };
  }

  function vehicleCard(root, vehicle, options) {
    const conditions = applicationConditions(root, vehicle);
    if (conditions.restricted) options = { ...options, status: conditions.title, tone: 'neutral' };
    const image = proxyVehicleImage(vehicleImage(vehicle));
    const title = vehicleName(vehicle);
    const meta = vehicleMeta(vehicle);
    const isDetailed = options && options.detailed;
    const extraClass = options && options.extraClass ? ` ${options.extraClass}` : '';
    const specs = [
      ['engineCode', readText(root, 'engineCodeLabel', 'Motorcode'), vehicle.engineCode || vehicle.EngineCode || ''],
      ['fuel', readText(root, 'fuelLabel', 'Kraftstoff'), vehicle.fuelType || vehicle.FuelType || vehicle.engineType || vehicle.EngineType || ''],
      ['capacity', readText(root, 'capacityLabel', 'Hubraum'), vehicle.capacityLiters || vehicle.CapacityLiters || vehicle.displacement || vehicle.Displacement || ''],
      ['power', readText(root, 'powerLabel', 'Leistung'), vehicle.powerKw || vehicle.PowerKw || vehicle.powerHp || vehicle.PowerHp
        ? [vehicle.powerKw || vehicle.PowerKw ? `${vehicle.powerKw || vehicle.PowerKw} kW` : '', vehicle.powerHp || vehicle.PowerHp ? `${vehicle.powerHp || vehicle.PowerHp} PS` : ''].filter(Boolean).join(' / ')
        : ''],
      ['year', readText(root, 'yearLabel', 'Baujahr'), vehicle.yearFrom || vehicle.YearFrom
        ? `${vehicle.yearFrom || vehicle.YearFrom}${vehicle.yearTo || vehicle.YearTo ? ` - ${vehicle.yearTo || vehicle.YearTo}` : ''}`
        : ''],
      ['body', readText(root, 'bodyLabel', 'Karosserie'), vehicle.bodyStyle || vehicle.BodyStyle || ''],
      ['drive', readText(root, 'driveLabel', 'Antrieb'), vehicle.driveType || vehicle.DriveType || ''],
      ['transmission', readText(root, 'transmissionLabel', 'Getriebe'), vehicle.transmission || vehicle.Transmission || ''],
      ['cylinders', readText(root, 'cylindersLabel', 'Zylinder'), vehicle.cylinders || vehicle.Cylinders ? String(vehicle.cylinders || vehicle.Cylinders) : ''],
      ['doors', readText(root, 'doorsLabel', 'Türen'), vehicle.doors || vehicle.Doors ? String(vehicle.doors || vehicle.Doors) : ''],
      ['fiscalHp', 'Fiscal HP', vehicle.fiscalHp || vehicle.FiscalHp || '']
    ].filter(([, , value]) => value);
    const body = specs
      .filter(([key]) => ['body', 'fuel', 'capacity', 'transmission'].includes(key))
      .map(([, , value]) => value)
      .filter(Boolean)
      .join(' - ');
    const status = options && options.status
      ? `<span class="dfn-product-fitment-details__badge" data-tone="${esc(options.tone || 'neutral')}">${esc(options.status)}</span>`
      : '';

    return `
      <article class="dfn-product-fitment-details__vehicle-card${isDetailed ? ' dfn-product-fitment-details__vehicle-card--detail' : ''}${extraClass}">
        <div class="dfn-product-fitment-details__vehicle-image">
          ${image ? `<img src="${esc(image)}" alt="${esc(title)}" loading="lazy">` : '<span aria-hidden="true"></span>'}
        </div>
        <div class="dfn-product-fitment-details__vehicle-copy">
          <div class="dfn-product-fitment-details__vehicle-title">
            <strong>${esc(title)}</strong>
            ${status}
          </div>
          ${meta ? `<p>${esc(meta)}</p>` : ''}
          ${body ? `<small>${esc(body)}</small>` : ''}
          ${conditions.html}
          ${isDetailed && specs.length ? `
            <dl class="dfn-product-fitment-details__specs">
              ${specs.map(([, label, value]) => `
                <div>
                  <dt>${esc(label)}</dt>
                  <dd>${esc(value)}</dd>
                </div>
              `).join('')}
            </dl>` : ''}
        </div>
      </article>`;
  }

  function isCompatibleSavedVehicle(vehicle, root) {
    if (!vehicle) return false;
    if ((vehicle.linkageTargetType || vehicle.LinkageTargetType) === 'M') return false;
    if (vehicle.storeId && vehicle.storeId !== root.dataset.storeId) return false;
    if (vehicle.tecDocProfile && vehicle.tecDocProfile !== root.dataset.tecDocProfile) return false;
    return true;
  }

  function currentLocalePrefix() {
    const firstSegment = window.location.pathname.split('/').filter(Boolean)[0] || '';
    const reserved = new Set(['apps', 'account', 'cart', 'collections', 'pages', 'products', 'search']);
    if (!firstSegment || reserved.has(firstSegment.toLowerCase())) return '';
    return /^[a-z]{2}(?:-[a-z]{2})?$/i.test(firstSegment) ? `/${firstSegment}` : '';
  }

  function localizeStorefrontPath(value) {
    const raw = String(value || '').trim();
    if (!raw || raw === '#') return raw || '#';

    try {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return raw;

      const prefix = currentLocalePrefix();
      if (!prefix || url.pathname.startsWith(`${prefix}/`) || url.pathname === prefix) {
        return `${url.pathname}${url.search}${url.hash}`;
      }

      if (/^\/(?:products|collections|pages|search)(?:\/|\?|$)/.test(url.pathname)) {
        return `${prefix}${url.pathname}${url.search}${url.hash}`;
      }

      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
      return raw;
    }
  }

  function buildResultsUrl(root, vehicle) {
    const id = vehicleId(vehicle);
    const url = new URL(root.dataset.resultsPageUrl || '/pages/fahrzeugergebnisse', window.location.origin);
    const family = vehicle && vehicle.family ? String(vehicle.family).trim() : '';
    url.searchParams.set('vehicleId', String(id || ''));
    url.searchParams.set('storeId', root.dataset.storeId || 'main-store');
    url.searchParams.set('tecDocProfile', root.dataset.tecDocProfile || 'de');
    if (family) {
      url.searchParams.set('family', family);
    } else {
      url.searchParams.delete('family');
    }
    return localizeStorefrontPath(`${url.pathname}${url.search}`);
  }

  async function apiFetch(path) {
    const cacheKey = path;
    const cached = requestCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.value;
    }

    const persisted = readSessionCache(cacheKey);
    if (persisted) {
      requestCache.set(cacheKey, { value: persisted, expiresAt: Date.now() + REQUEST_CACHE_TTL });
      return persisted;
    }

    if (inflightRequests.has(cacheKey)) {
      return inflightRequests.get(cacheKey);
    }

    const request = (async () => {
      const response = await fetch(`${API_BASE}${path}`);
      let json = null;

      try {
        json = await response.json();
      } catch (_) {
        json = null;
      }

      if (!response.ok || !json || json.success === false) {
        throw createApiError(response, json, `API error ${response.status}`);
      }

      const data = json.data;
      requestCache.set(cacheKey, { value: data, expiresAt: Date.now() + REQUEST_CACHE_TTL });
      writeSessionCache(cacheKey, data, REQUEST_CACHE_TTL);
      return data;
    })().finally(() => inflightRequests.delete(cacheKey));

    inflightRequests.set(cacheKey, request);
    return request;
  }

  function setState(root, tone, title, text, vehicle) {
    const status = qs(root, '[data-fitment-status]');
    const titleNode = qs(root, '[data-fitment-title]');
    const textNode = qs(root, '[data-fitment-text]');
    const vehicleNode = qs(root, '[data-fitment-vehicle]');

    if (status) status.dataset.fitmentStatus = tone || 'idle';
    if (titleNode) titleNode.textContent = title || '';
    if (textNode) textNode.textContent = text || '';

    if (vehicleNode) {
      if (vehicle) {
        vehicleNode.classList.remove('dfn-hidden');
        vehicleNode.innerHTML = root.dataset.showVehicleCard === 'true'
          ? vehicleCard(root, vehicle)
          : `
            <strong>${esc(vehicleName(vehicle))}</strong>
            ${vehicleMeta(vehicle) ? `<span>${esc(vehicleMeta(vehicle))}</span>` : ''}`;
      } else {
        vehicleNode.classList.add('dfn-hidden');
        vehicleNode.innerHTML = '';
      }
    }
  }

  function renderActions(root, vehicle, mode) {
    const actions = qs(root, '[data-fitment-actions]');
    if (!actions) return;

    const pieces = [];
    if (mode === 'no-vehicle') {
      pieces.push(`<button type="button" class="btn btn--primary" data-fitment-open>${esc(readText(root, 'selectLabel', 'Fahrzeug auswählen'))}</button>`);
    } else if (vehicle) {
      pieces.push(`<a class="btn btn--primary" href="${esc(buildResultsUrl(root, vehicle))}">${esc(readText(root, 'resultsLabel', 'Passende Teile anzeigen'))}</a>`);
      pieces.push(`<button type="button" class="btn btn--secondary" data-fitment-open>${esc(readText(root, 'changeLabel', 'Fahrzeug aendern'))}</button>`);
      pieces.push(`<button type="button" class="link dfn-product-fitment__reset" data-fitment-reset>${esc(readText(root, 'resetLabel', 'Fahrzeug zurücksetzen'))}</button>`);
    }

    actions.innerHTML = pieces.join('');

    qsa(actions, '[data-fitment-open]').forEach((button) => {
      button.addEventListener('click', openFinder);
    });

    qsa(actions, '[data-fitment-reset]').forEach((button) => {
      button.addEventListener('click', clearSavedVehicle);
    });
  }

  async function checkFitment(root) {
    const sku = String(root.dataset.sku || '').trim();
    const vehicle = getSavedVehicle();

    if (!vehicle || !isCompatibleSavedVehicle(vehicle, root)) {
      setState(root, 'idle', readText(root, 'noVehicleTitle', 'Passt dieses Teil zu Ihrem Fahrzeug?'), readText(root, 'noVehicleText', 'Wählen Sie Ihr Fahrzeug aus. Danach prüfen wir automatisch, ob dieses Produkt passt.'), null);
      renderActions(root, null, 'no-vehicle');
      return;
    }

    if (!sku) {
        setState(root, 'unknown', readText(root, 'unknownTitle', 'Kompatibilität konnte nicht geprüft werden'), readText(root, 'missingSkuText', 'Für diese Variante ist keine SKU hinterlegt.'), vehicle);
      renderActions(root, vehicle, 'known-vehicle');
      return;
    }

    setState(root, 'loading', readText(root, 'checkingText', 'Kompatibilität wird geprüft...'), displaySku(sku), vehicle);
    renderActions(root, vehicle, 'known-vehicle');

    try {
      const result = await fetchProductCompatibility(root, vehicle, sku);
      if (result && result.requiresVerification) {
        const conditions = applicationConditions(root, result.vehicle || vehicle);
        setState(root, 'unknown', conditions.title, String(root.dataset.language || '').startsWith('fr')
          ? 'Vérifiez les conditions du véhicule ci-dessous avant de commander.'
          : 'Bitte prüfen Sie vor der Bestellung die unten aufgeführten Fahrzeugeinschränkungen.', result.vehicle || vehicle);
      } else if (result && result.isCompatible) {
        setState(root, 'compatible', readText(root, 'compatibleTitle', 'Passt zu Ihrem Fahrzeug'), formatText(readText(root, 'compatibleText', '{sku} ist für Ihre Fahrzeugauswahl freigegeben.'), { sku: displaySku(sku) }), vehicle);
      } else {
        setState(root, 'incompatible', readText(root, 'incompatibleTitle', 'Passt nicht zu Ihrem Fahrzeug'), formatText(readText(root, 'incompatibleText', '{sku} ist für Ihre Fahrzeugauswahl aktuell nicht als passend hinterlegt.'), { sku: displaySku(sku) }), vehicle);
      }
    } catch (error) {
      setState(root, 'unknown', readText(root, 'unknownTitle', 'Kompatibilität konnte nicht geprüft werden'), resolveUserErrorMessage(root, error, readText(root, 'apiGenericText', 'Die Daten konnten gerade nicht geladen werden. Bitte versuchen Sie es erneut.')), vehicle);
    }
  }

  function detailsBody(root) {
    return qs(root, '[data-fitment-details-body]');
  }

  function renderDetailsError(root, message) {
    const body = detailsBody(root);
    if (!body) return;
    body.innerHTML = `<div class="dfn-product-fitment-details__empty">${esc(message || readText(root, 'emptyText', 'Keine Daten gefunden.'))}</div>`;
  }

  function bindDetailsActions(root) {
    qsa(root, '[data-fitment-open]').forEach((button) => {
      button.addEventListener('click', openFinder);
    });
  }

  function bindVehicleListToggle(root, remainingVehicles) {
    const button = qs(root, '[data-fitment-show-more]');
    const list = qs(root, '[data-fitment-vehicle-list]');
    if (!button || !list) return;

    const showText = button.dataset.showText || 'Weitere Fahrzeuge anzeigen';
    const hideText = button.dataset.hideText || 'Weniger anzeigen';
    const count = button.dataset.remainingCount || '';

    button.addEventListener('click', () => {
      const expanded = !list.classList.contains('is-expanded');
      if (expanded && list.dataset.moreMounted !== 'true') {
          list.insertAdjacentHTML('beforeend', remainingVehicles
          .map((vehicle) => vehicleCard(root, vehicle, { extraClass: 'dfn-product-fitment-details__vehicle-card--more' }))
          .join(''));
        list.dataset.moreMounted = 'true';
      } else if (!expanded) {
        qsa(list, '.dfn-product-fitment-details__vehicle-card--more').forEach((card) => card.remove());
        list.dataset.moreMounted = 'false';
      }

      list.classList.toggle('is-expanded', expanded);
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      button.textContent = expanded ? hideText : `${showText}${count ? ` (${count})` : ''}`;
    });
  }

  async function fetchFitmentDetails(root) {
    const sku = String(root.dataset.sku || '').trim();
    if (!sku) throw new Error(readText(root, 'missingSkuText', 'Fuer diese Variante ist keine SKU hinterlegt.'));

    const params = new URLSearchParams({
      sku,
      storeId: root.dataset.storeId || 'main-store',
      tecDocProfile: root.dataset.tecDocProfile || 'de',
      lang: root.dataset.language || document.documentElement.lang || 'de',
      limit: String(root.dataset.limit || 80)
    });

    const cacheKey = params.toString();
    if (!fitmentDetailsCache.has(cacheKey)) {
      fitmentDetailsCache.set(cacheKey, apiFetch(`/product-fitment-details?${cacheKey}`));
    }

    return fitmentDetailsCache.get(cacheKey);
  }

  function fetchProductCompatibility(root, vehicle, sku) {
    const params = new URLSearchParams({
      vehicleId: String(vehicleId(vehicle)),
      linkageTargetType: vehicle.linkageTargetType || vehicle.LinkageTargetType || 'P',
      sku,
      storeId: root.dataset.storeId || 'main-store',
      tecDocProfile: root.dataset.tecDocProfile || 'de',
      lang: root.dataset.language || document.documentElement.lang || 'de'
    });
    const cacheKey = params.toString();

    if (!productCompatibilityCache.has(cacheKey)) {
      productCompatibilityCache.set(cacheKey, apiFetch(`/product-compatibility?${cacheKey}`));
    }

    return productCompatibilityCache.get(cacheKey);
  }

  async function renderSelectedVehicleDetails(root) {
    const body = detailsBody(root);
    if (!body) return;

    const vehicle = getSavedVehicle();
    if (!vehicle || !isCompatibleSavedVehicle(vehicle, root)) {
      try {
        const result = await fetchFitmentDetails(root);
        const firstVehicle = result && result.vehicles && result.vehicles[0];
        if (firstVehicle) {
          body.innerHTML = `
            <div class="dfn-product-fitment-details__summary">
              <strong>${esc(applicationConditions(root, firstVehicle).restricted ? applicationConditions(root, firstVehicle).title : readText(root, 'unselectedExampleText', 'Noch kein Fahrzeug gewaehlt. Wir zeigen ein kompatibles Beispielfahrzeug.'))}</strong>
              <button type="button" class="btn btn--secondary" data-fitment-open>${esc(readText(root, 'selectLabel', 'Fahrzeug auswaehlen'))}</button>
            </div>
            ${vehicleCard(root, firstVehicle, { status: readText(root, 'compatibleVehicleLabel', 'Kompatibles Fahrzeug'), tone: 'ok', detailed: true })}`;
          bindDetailsActions(root);
          return;
        }
      } catch (_) {}

      renderDetailsError(root, readText(root, 'emptyText', 'Noch kein Fahrzeug gewaehlt.'));
      return;
    }

    body.innerHTML = vehicleCard(root, vehicle, { status: readText(root, 'waitingCheckLabel', 'Wird geprueft'), tone: 'loading' });

    const sku = String(root.dataset.sku || '').trim();
    if (!sku) return;

    try {
      const result = await fetchProductCompatibility(root, vehicle, sku);
      if (result && result.requiresVerification) {
        body.innerHTML = vehicleCard(root, result.vehicle || vehicle, { detailed: true });
        return;
      }
      if (result && result.isCompatible) {
        body.innerHTML = vehicleCard(root, result.vehicle || vehicle, { status: readText(root, 'matchingLabel', 'Passend'), tone: 'ok', detailed: true });
        return;
      }

      let fallbackHtml = '';
      try {
        const details = await fetchFitmentDetails(root);
        const firstVehicle = details && details.vehicles && details.vehicles[0];
        fallbackHtml = firstVehicle
          ? `<div class="dfn-product-fitment-details__summary">${esc(readText(root, 'fallbackVehicleText', 'Stattdessen ist dieses Produkt z. B. fuer folgendes Fahrzeug hinterlegt.'))}</div>${vehicleCard(root, firstVehicle, { status: readText(root, 'compatibleVehicleLabel', 'Kompatibles Fahrzeug'), tone: 'ok', detailed: true })}`
          : '';
      } catch (_) {}

      body.innerHTML = `${vehicleCard(root, vehicle, { status: readText(root, 'notMatchingLabel', 'Nicht passend'), tone: 'bad', detailed: true })}${fallbackHtml}`;
    } catch (_) {
      body.innerHTML = vehicleCard(root, vehicle, { status: readText(root, 'notCheckedLabel', 'Nicht geprueft'), tone: 'neutral', detailed: true });
    }
  }

  function renderOemDetails(root, result) {
    const body = detailsBody(root);
    if (!body) return;

    const oems = (result && result.oemNumbers) || [];
    if (!oems.length) {
      renderDetailsError(root, readText(root, 'noOemsText', 'Fuer diese Variante sind keine OEM-Nummern hinterlegt.'));
      return;
    }

    body.innerHTML = `
      <div class="dfn-product-fitment-details__chips">
        ${oems.map((oem) => `
          <span class="dfn-product-fitment-details__chip">
            <strong>${esc(oem.articleNumber || oem.ArticleNumber || '')}</strong>
            ${(oem.brand || oem.Brand) ? `<small>${esc(oem.brand || oem.Brand)}</small>` : ''}
          </span>
        `).join('')}
      </div>`;
  }

  function renderVehicleListDetails(root, result) {
    const body = detailsBody(root);
    if (!body) return;

    const vehicles = (result && result.vehicles) || [];
    if (!vehicles.length) {
      renderDetailsError(root, readText(root, 'noVehiclesText', 'Fuer diese Variante sind keine kompatiblen Fahrzeuge hinterlegt.'));
      return;
    }

    const total = result.totalVehicles || vehicles.length;
    const hasRestrictions = vehicles.some(vehicle => applicationConditions(root, vehicle).restricted);
    const motorOnly = vehicles.every(v => (v.linkageTargetType || v.LinkageTargetType) === 'M');
    const motorLanguage = String(root.dataset.language || document.documentElement.lang || 'de').slice(0, 2);
    const motorCount = {de: '{count} Motoranwendungen — Bedingungen prüfen', en: '{count} engine applications — check conditions', fr: '{count} applications moteur — vérifier les conditions', it: '{count} applicazioni motore — verificare le condizioni', es: '{count} aplicaciones de motor — comprobar las condiciones'};
    const countText = motorOnly ? (motorCount[motorLanguage] || motorCount.en) : hasRestrictions
      ? (String(root.dataset.language || '').startsWith('fr') ? '{count} véhicules au catalogue — conditions à vérifier' : '{count} Fahrzeuge im Katalog — Einschränkungen beachten')
      : readText(root, 'compatibleCountText', '{count} kompatible Fahrzeuge in TecDoc hinterlegt');
    const initialVisible = Math.max(1, Number(root.dataset.initialVisible || 6) || 6);
    const visibleVehicles = vehicles.slice(0, initialVisible);
    const remainingVehicles = vehicles.slice(initialVisible);
    const hiddenCount = remainingVehicles.length;
    const showMoreHtml = hiddenCount > 0
      ? `<div class="dfn-product-fitment-details__vehicle-list-actions">
          <button type="button" class="btn btn--secondary" data-fitment-show-more data-show-text="${esc(readText(root, 'showMoreLabel', 'Weitere Fahrzeuge anzeigen'))}" data-hide-text="${esc(readText(root, 'showLessLabel', 'Weniger anzeigen'))}" data-remaining-count="${hiddenCount}" aria-expanded="false">${esc(readText(root, 'showMoreLabel', 'Weitere Fahrzeuge anzeigen'))} (${hiddenCount})</button>
        </div>`
      : '';

    body.innerHTML = `
      <div class="dfn-product-fitment-details__summary">
        ${esc(formatText(countText, { count: total }))}
      </div>
      <div class="dfn-product-fitment-details__vehicle-list" data-fitment-vehicle-list>
        ${visibleVehicles.map((vehicle) => vehicleCard(root, vehicle)).join('')}
      </div>
      ${showMoreHtml}`;

    bindVehicleListToggle(root, remainingVehicles);
  }

  async function loadDetailsRoot(root) {
    const body = detailsBody(root);
    if (!body) return;
    if (root.dataset.fitmentDetailsLoaded === 'true') return;
    root.dataset.fitmentDetailsLoaded = 'true';

    const mode = root.dataset.fitmentDetailsMode || 'vehicles';
    body.innerHTML = `<div class="dfn-product-fitment-details__loading">${esc(readText(root, 'loadingText', 'Daten werden geladen...'))}</div>`;

    if (mode === 'selected_vehicle') {
      await renderSelectedVehicleDetails(root);
      return;
    }

    try {
      const result = await fetchFitmentDetails(root);
      if (mode === 'oem') {
        renderOemDetails(root, result);
      } else {
        renderVehicleListDetails(root, result);
      }
    } catch (error) {
      renderDetailsError(root, resolveUserErrorMessage(root, error, readText(root, 'apiGenericText', 'Die Daten konnten gerade nicht geladen werden. Bitte versuchen Sie es erneut.')));
    }
  }

  function initDetailsRoot(root) {
    if (!root || root.dataset.fitmentDetailsReady === 'true') return;
    root.dataset.fitmentDetailsReady = 'true';
    const disclosure = root.querySelector('details');
    if (disclosure) {
      disclosure.addEventListener('toggle', () => {
        if (disclosure.open) loadDetailsRoot(root);
      });
      if (!disclosure.open) return;
    }

    loadDetailsRoot(root);
  }

  function initRoot(root) {
    if (!root || root.dataset.fitmentReady === 'true') return;
    root.dataset.fitmentReady = 'true';
    checkFitment(root);
  }

  function initAll() {
    qsa(document, '[data-dfn-product-fitment="true"]').forEach(initRoot);
    qsa(document, '[data-dfn-product-fitment-details="true"]').forEach(initDetailsRoot);
  }

  function refreshFitmentState() {
    initAll();
    qsa(document, '[data-dfn-product-fitment="true"]').forEach((root) => checkFitment(root));
    qsa(document, '[data-dfn-product-fitment-details="true"]').forEach((root) => {
      root.dataset.fitmentDetailsLoaded = 'false';
      const disclosure = root.querySelector('details');
      if (!disclosure || disclosure.open) loadDetailsRoot(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('dfn:vehicle-selected', () => {
    qsa(document, '[data-dfn-product-fitment="true"]').forEach((root) => checkFitment(root));
    qsa(document, '[data-dfn-product-fitment-details="true"]').forEach((root) => {
      root.dataset.fitmentDetailsLoaded = 'false';
      const disclosure = root.querySelector('details');
      if (!disclosure || disclosure.open) loadDetailsRoot(root);
    });
  });
  document.addEventListener('dfn:vehicle-cleared', () => {
    qsa(document, '[data-dfn-product-fitment="true"]').forEach((root) => checkFitment(root));
    qsa(document, '[data-dfn-product-fitment-details="true"]').forEach((root) => {
      root.dataset.fitmentDetailsLoaded = 'false';
      const disclosure = root.querySelector('details');
      if (!disclosure || disclosure.open) loadDetailsRoot(root);
    });
  });
  document.addEventListener('on:variant:change', () => {
    window.setTimeout(refreshFitmentState, 0);
  });
  document.addEventListener('on:variant:after-replace-element', () => {
    window.setTimeout(refreshFitmentState, 0);
  });
})();
